import AnnouncementIcon from '@mui/icons-material/Announcement';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material";
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { KeyboardKey } from '_shared/_utilities/keyboard-keys';

const SearchField = styled(TextField)(({ theme }) => ({
  backdropFilter: "blur(10px) brightness(105%)",
  backgroundColor: "rgba(255, 255, 255, 0.82)",
  bottom: theme.spacing(1),
  position: 'sticky',
  top: '1px',
  width: '100%',
  zIndex: 10,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
  },
}));

type SearchBarProps = {
  className?: string;
  showSystemMessages: boolean;
  showAttachments: boolean;
  filterDates: boolean;
  onToggleDates: () => void;
  onToggleSystemMessages: () => void;
  onToggleAttachments: () => void;
  onSearch: (searchString: string) => void;
};

export default function SearchBar(props: Readonly<SearchBarProps>) {
  const {
    className,
    showSystemMessages,
    showAttachments,
    filterDates,
    onToggleDates,
    onToggleSystemMessages,
    onToggleAttachments,
    onSearch
  } = props;

  const [searchString, setSearchString] = useState<string>('');

  const debouncedSearch = useMemo(() => _.debounce((value: string) => onSearch(value), 500), [onSearch]);

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  function handleSearchStringChanged(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setSearchString(value);
    debouncedSearch(value);
  }

  function clearSearch() {
    debouncedSearch.cancel();
    setSearchString('');
    onSearch('');
  }

  return (
    <SearchField
      className={className}
      variant="outlined"
      placeholder="Search"
      size="small"
      fullWidth={true}
      onChange={handleSearchStringChanged}
      onKeyDown={(event) => {
        if (event.key === KeyboardKey.Enter) {
          onSearch(searchString);
        }
        if (event.key === KeyboardKey.Escape) {
          clearSearch();
        }
      }}
      value={searchString}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color='disabled' />
            </InputAdornment>
          ),
          endAdornment: (
            <>
              {searchString && (
                <Tooltip
                  arrow
                  title="Clear Search"
                >
                  <IconButton
                    size="small"
                    onClick={clearSearch}>
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                arrow
                title={
                  showSystemMessages ? "Show All Messages" : "Only Show System Messages"
                }
              >
                <IconButton
                  size="small"
                  onClick={onToggleSystemMessages}>
                  <AnnouncementIcon
                    color={showSystemMessages ? 'primary' : 'inherit'}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip
                arrow
                title={
                  showAttachments ? "Show All Messages" : "Only Show Attachments"
                }
              >
                <IconButton
                  size="small"
                  onClick={onToggleAttachments}>
                  <AttachFileIcon
                    color={showAttachments ? 'primary' : 'inherit'}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip
                arrow
                title={
                  filterDates ? "Stop Filtering By Date" : "Filter By Date"
                }
              >
                <IconButton
                  size="small"
                  onClick={onToggleDates}>
                  <EventIcon 
                    color={filterDates ? 'primary' : 'inherit'}
                  />
                </IconButton>
              </Tooltip>
            </>
          )
        }
      }}
    />
  )
}
